<template>

<app-content :loading="is.loading">

	<app-content-head :title="title" :back="$route.meta.back" :readonly="$authorised('con/schedule/access', 'read')">

		<app-content-head-actions-item disabled="!isDirty" icon="undo" text="Undo" v-on:click="onUndoClick" />
		<app-content-head-actions-item :loading="is.saving" :disabled="!isDirty || !isValid" icon="save" text="Save" v-on:click="onSaveClick" />
		<app-content-head-actions-item v-if="!isNew" :disabled="isNew" :danger="true" icon="delete" text="Delete" v-on:click="onDeleteClick" />

	</app-content-head>

	<app-content-body v-if="is.initialised">

		<app-content-box :readonly="$authorised('con/schedule/access', 'read')">

			<app-input-text label="Name" placeholder="Enter name..." v-model="model.name" :validation="$v.model.name" />
			
			<app-input-checklist label="Participation" v-model="model.type" :validation="$v.model.type" :options="$constants.schedule.contest.typeName" :descriptions="$constants.schedule.contest.typeDescription" :fullWidth="true" :singular="true" :columns="1" />
			
			<app-input-checklist label="Win Eligibility" v-model="model.qualification.enabled" :validation="$v.model.qualification.enabled" :options="$constants.schedule.contest.qualification.enabledName" :descriptions="$constants.schedule.contest.qualification.enabledDescription" :fullWidth="true" :singular="true" :columns="1" />
			
			<app-input-checklist label="Prize Awarding" v-model="model.auto.enabled" :validation="$v.model.auto.enabled" :options="$constants.schedule.contest.awarding.enabledName" :descriptions="$constants.schedule.contest.awarding.enabledDescription" :fullWidth="true" :singular="true" :columns="1" />
			
		</app-content-box>

		<app-content-box :tabs="textTabs" :readonly="$authorised('con/schedule/access', 'read')">

			<template v-slot:dates>

				<app-input-date v-if="model.type === $constants.schedule.contest.type.register" :time="true" label="Registration" placeholder="Enter register date..." notes="When registration opens for participants. If no date is provided the registration is immediately open." :toggle="model.register.enabled" v-on:toggle="model.register.enabled = !model.register.enabled" v-model="model.register.date" :validation="$v.model.register.date" />
				
				<app-input-date v-if="model.type === $constants.schedule.contest.type.register" :time="true" label="Starts" placeholder="Enter start date..." notes="When registration ends and the contest starts. If no date is provided the contest is immediately open." v-model="model.from.date" :validation="$v.model.from.date" />
				
				<app-input-date v-if="model.type !== $constants.schedule.contest.type.register" :time="true" label="Starts" placeholder="Enter start date..." notes="When the contest starts and QR code scans are counted. If no date is provided the contest is immediately open." :toggle="model.from.enabled" v-on:toggle="model.from.enabled = !model.from.enabled" v-model="model.from.date" :validation="$v.model.from.date" />
				
				<app-input-date :time="true" label="Ends" placeholder="Enter end date..." notes="When the contest ends and QR code scans are no longer counted. If no date is provided the contest runs until all prizes are awarded." :toggle="model.to.enabled" v-on:toggle="model.to.enabled = !model.to.enabled" v-model="model.to.date" :validation="$v.model.to.date" />
				
				<app-input-date v-if="model.auto.enabled" :time="true" label="Awards" notes="The date the prizes are automatically awarded to eligible participants by the system. Individual prizes can override this value." placeholder="Enter award date..." v-model="model.auto.date" :validation="$v.model.auto.date" />
				
			</template>

			<template v-slot:eligibility>
				
				<app-input-checklist label="Type" v-model="model.qualification.type" :validation="$v.model.qualification.type" :options="$constants.schedule.contest.qualification.typeName" :descriptions="$constants.schedule.contest.qualification.typeDescription" :fullWidth="true" :singular="true" :columns="1" />
				
				<app-input-text v-if="model.qualification.type === $constants.schedule.contest.qualification.type.threshold" label="Threshold" placeholder="Enter number..." notes="The minimum QR codes scanned to be eligible. Individual QR codes can be weighted with a higher value." v-model="model.qualification.threshold" :validation="$v.model.qualification.threshold" />
				
				<app-input-checklist label="Sequence" v-model="model.qualification.order" :validation="$v.model.qualification.order" :options="$constants.schedule.contest.qualification.orderName" :descriptions="$constants.schedule.contest.qualification.orderDescription" :fullWidth="true" :singular="true" :columns="1" />
				
			</template>

			<template v-slot:awarding>
				
				<app-input-checklist label="Probability" v-model="model.awarding.probability" :validation="$v.model.awarding.probability" :options="$constants.schedule.contest.awarding.probabilityName" :descriptions="$constants.schedule.contest.awarding.probabilityDescription" :fullWidth="true" :singular="true" :columns="1" />
				
				<app-input-checklist label="Repeats" v-model="model.awarding.repeats" :validation="$v.model.awarding.repeats" :options="$constants.schedule.contest.awarding.repeatsName" :descriptions="$constants.schedule.contest.awarding.repeatsDescription" :fullWidth="true" :singular="true" :columns="1" />
				
			</template>

			<template v-slot:info>

				<app-input-text label="Description" :autogrow="true" placeholder="Enter description..." v-model="model.description" :validation="$v.model.description" />
				<app-input-image label="Image" placeholder="Enter image..." v-model="model.image" :validation="$v.model.image" />

			</template>

		</app-content-box>

		<app-content-section v-if="!isNew" route="Convention.Schedule.Contest.Codes" :params="{ contest: item.id }" title="QR Codes" icon="checkout" description="Manage the QR codes that can be scanned to participate." :count="item.count.codes" :danger="!item.count.codes" />
		<app-content-section v-if="!isNew" route="Convention.Schedule.Contest.Prizes" :params="{ contest: item.id }" title="Prizes" icon="prize" description="Manage the prizes than can be won by eligible participants." :count="item.count.prizes" :danger="!item.count.prizes" />
		<app-content-section v-if="!isNew" route="Convention.Schedule.Contest.Winners" :params="{ contest: item.id }" title="Winners" icon="winner" description="Manage the winners and manually award prizes." :count="item.count.winners" />

	</app-content-body>

</app-content>

</template>

<script>

import mixForm from '@/mixin/form'
import { required } from 'vuelidate/lib/validators'

export default {

	mixins: [mixForm],

	data: function() {

		return {
			readonly: this.$authorised('con/schedule/access', 'read'),
			model: {
				id: false,
				name: '',
				status: 0,
				type: 0,
				image: '',
				description: '',
				to: {
					enabled: 0,
					date: 0
				},
				auto: {
					enabled: 0,
					date: 0
				},
				register: {
					enabled: 0,
					date: 0
				},
				from: {
					enabled: 0,
					date: 0
				},
				awarding: {
					probability: 1,
					repeats: 2
				},
				qualification: {
					enabled: 0,
					type: 1,
					order: 1,
					threshold: 0
				}
			}
		}

	},

	validations: {
		model: {
			name: {
				required
			},
			description: {
				required
			},
			image: {},
			type: {
				required
			},
			awarding: {
				probability: {},
				repeats: {}
			},
			to: {
				date: {}
			},
			register: {
				date: {}
			},
			auto: {
				date: {}
			},
			from: {
				date: {}
			},
			qualification: {
				enabled: {},
				type: {},
				threshold: {}
			}
		}
	},

	computed: {

		textTabs: function() {

			return [
				{ name: 'dates', text: 'Dates' },
				{ name: 'eligibility', text: 'Eligibility', enabled: this.model.qualification.enabled === 1 },
				{ name: 'awarding', text: 'Awarding' },
				{ name: 'info', text: 'Information' }
			]

		}

	}

}

</script>

<style scoped>

</style>